import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Stack,
  Skeleton,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  FilterList as FilterListIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useUserManagement } from '../../hooks/useUserManagement';
import { UserManagementData } from '../../types/admin';
import { UserRole, UserStatus } from '../../types/auth';
import { AdminHeader } from './AdminHeader';
import { UserChatHistory } from './index';
import { UserProfileDetailModal } from './UserProfileDetailModal';
import { getIntegratedUserStatus } from '../MyPage/Profile/utils';

export const UserManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    users,
    totalCount,
    currentPage,
    pageSize,
    filters,
    loading,
    error,
    fetchUsers,
    updateUserRole,
    toggleUserActive,
    setPage,
    setFilters,
    clearError,
  } = useUserManagement();

  const [searchQuery, setSearchQuery] = useState(filters.userSearch || '');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>(
    filters.userRole || 'all'
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserManagementData | null>(
    null
  );
  const [editRole, setEditRole] = useState<UserRole>(UserRole.STUDENT);
  const [editActive, setEditActive] = useState(true);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [chatHistoryUser, setChatHistoryUser] =
    useState<UserManagementData | null>(null);
  const [profileDetailOpen, setProfileDetailOpen] = useState(false);
  const [profileDetailUserId, setProfileDetailUserId] = useState<number | null>(
    null
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setFilters({
      ...filters,
      userSearch: searchQuery.trim() || undefined,
      userRole: roleFilter === 'all' ? undefined : roleFilter,
    });
  };

  const handleEditUser = (user: UserManagementData) => {
    setSelectedUser(user);
    setEditRole(user.userRole);
    setEditActive(user.status === 'active');
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    let success = true;

    // Update role if changed
    if (editRole !== selectedUser.userRole) {
      success = await updateUserRole(selectedUser.id, editRole);
    }

    // Update active status if changed
    const currentActive = selectedUser.status === UserStatus.ACTIVE;
    if (success && editActive !== currentActive) {
      success = await toggleUserActive(selectedUser.id, editActive);
    }

    if (success) {
      setEditDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleViewChatHistory = (user: UserManagementData) => {
    setChatHistoryUser(user);
    setChatHistoryOpen(true);
  };

  const handleCloseChatHistory = () => {
    setChatHistoryOpen(false);
    setChatHistoryUser(null);
  };

  const handleViewProfile = (user: UserManagementData) => {
    setProfileDetailUserId(user.id);
    setProfileDetailOpen(true);
  };

  const handleCloseProfile = () => {
    setProfileDetailOpen(false);
    setProfileDetailUserId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return isMobile
      ? date.toLocaleDateString('ja-JP', {
          month: 'short',
          day: 'numeric',
        })
      : date.toLocaleDateString('ja-JP');
  };

  const getRoleChip = (role: UserRole) => {
    const getRoleLabel = (role: UserRole) => {
      switch (role) {
        case UserRole.ADMIN:
          return '管理者';
        case UserRole.COMPANY_ADMIN:
          return '企業管理者';
        case UserRole.HR:
          return 'HR';
        case UserRole.EMPLOYEE:
          return '従業員';
        case UserRole.STUDENT:
        default:
          return '一般ユーザー';
      }
    };

    return (
      <Chip
        label={getRoleLabel(role)}
        color={role === UserRole.ADMIN ? 'primary' : 'default'}
        size={isMobile ? 'small' : 'small'}
        sx={{
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          height: { xs: 20, sm: 24 },
          '& .MuiChip-label': {
            px: { xs: 0.5, sm: 1 },
          },
        }}
      />
    );
  };

  const getStatusChip = (status: UserStatus, isVerified: boolean) => {
    const statusInfo = getIntegratedUserStatus(status, isVerified);
    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color}
        size={isMobile ? 'small' : 'small'}
        variant="filled"
        sx={{
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          height: { xs: 20, sm: 24 },
          '& .MuiChip-label': {
            px: { xs: 0.5, sm: 1 },
          },
        }}
      />
    );
  };

  return (
    <Box>
      <AdminHeader
        title="ユーザー管理"
        description="システムユーザーの管理、権限設定、アカウント状態の変更を行います"
        pageType="users"
      />

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 検索・フィルター */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, mt: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
        >
          検索・フィルター
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'end' }}
        >
          <TextField
            label="ユーザー名・メールアドレス"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 200,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              ),
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size={isMobile ? 'small' : 'medium'}
          />

          <FormControl
            sx={{ minWidth: { xs: '100%', sm: 120 } }}
            size={isMobile ? 'small' : 'medium'}
          >
            <InputLabel>権限</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) =>
                setRoleFilter(e.target.value as 'all' | UserRole)
              }
              label="権限"
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value={UserRole.STUDENT}>一般ユーザー</MenuItem>
              <MenuItem value={UserRole.COMPANY_ADMIN}>企業管理者</MenuItem>
              <MenuItem value={UserRole.HR}>HR担当者</MenuItem>
              <MenuItem value={UserRole.EMPLOYEE}>従業員</MenuItem>
              <MenuItem value={UserRole.ADMIN}>管理者</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<FilterListIcon />}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 100 },
            }}
          >
            検索
          </Button>
        </Stack>
      </Paper>

      {/* ユーザー一覧テーブル */}
      <TableContainer
        component={Paper}
        sx={{
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: { xs: 700, sm: 800 },
          },
        }}
      >
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ minWidth: { xs: 100, sm: 120 }, fontWeight: 600 }}
              >
                氏名
              </TableCell>
              <TableCell
                sx={{ minWidth: { xs: 150, sm: 200 }, fontWeight: 600 }}
              >
                メールアドレス
              </TableCell>
              <TableCell
                sx={{ minWidth: { xs: 80, sm: 100 }, fontWeight: 600 }}
              >
                権限
              </TableCell>
              <TableCell
                sx={{ minWidth: { xs: 80, sm: 100 }, fontWeight: 600 }}
              >
                状態
              </TableCell>
              <TableCell
                sx={{ minWidth: { xs: 80, sm: 100 }, fontWeight: 600 }}
              >
                登録日
              </TableCell>
              <TableCell
                sx={{ minWidth: { xs: 90, sm: 120 }, fontWeight: 600 }}
              >
                最終ログイン / 退会日
              </TableCell>
              <TableCell
                sx={{
                  minWidth: { xs: 60, sm: 80 },
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                チャット数
              </TableCell>
              <TableCell
                sx={{
                  minWidth: { xs: 80, sm: 100 },
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 8 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" width="100%" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : users && users.length > 0 ? (
              users.map((user) => {
                const isDeleted = user.deletedAt && user.deletedAt !== null && user.deletedAt !== '';
                
                return (
                  <TableRow 
                    key={user.id} 
                    hover={!isDeleted}
                    sx={{
                      backgroundColor: isDeleted ? '#f5f5f5' : 'inherit',
                      opacity: isDeleted ? 0.75 : 1,
                      '&:hover': {
                        backgroundColor: isDeleted ? '#eeeeee' : undefined
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        maxWidth: { xs: 100, sm: 150 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      }}
                      title={user.fullName}
                    >
                      {user.fullName}
                      {isDeleted && (
                        <Box component="span" sx={{ ml: 0.5, color: 'error.main', fontWeight: 'bold', fontSize: '0.7rem' }}>
                          (退会)
                        </Box>
                      )}
                    </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: { xs: 150, sm: 200 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                    title={user.email}
                  >
                    {user.email}
                  </TableCell>
                  <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                    {getRoleChip(user.userRole)}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {isDeleted ? (
                        <Chip
                          label="退会済み"
                          color="error"
                          variant="filled"
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            backgroundColor: '#d32f2f',
                            color: 'white'
                          }}
                        />
                      ) : (
                        getStatusChip(user.status, user.isVerified)
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: isDeleted ? 'text.secondary' : 'inherit' }}
                  >
                    {formatDate(user.registeredAt || user.createdAt)}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {isDeleted && user.deletedAt ? (
                      <Tooltip title={`退会日時: ${formatDate(user.deletedAt)}`}>
                        <Box sx={{ color: 'error.main', fontWeight: 'medium' }}>
                          {formatDate(user.deletedAt)}
                        </Box>
                      </Tooltip>
                    ) : user.lastLoginAt ? (
                      formatDate(user.lastLoginAt)
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      color: isDeleted ? 'text.secondary' : 'inherit'
                    }}
                  >
                    {user.totalChats || 0}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 0, sm: 0.5 },
                        justifyContent: 'center',
                      }}
                    >
                      <Tooltip title="チャット履歴を表示">
                        <IconButton
                          onClick={() => handleViewChatHistory(user)}
                          size={isMobile ? 'small' : 'small'}
                          color="info"
                          disabled={isDeleted}
                          sx={{
                            padding: { xs: 0.5, sm: 1 },
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '1rem', sm: '1.2rem' },
                            },
                          }}
                        >
                          <ChatIcon />
                        </IconButton>
                      </Tooltip>
                      {!isDeleted && (
                        <Tooltip title="ユーザー情報を編集">
                          <IconButton
                            onClick={() => handleEditUser(user)}
                            size={isMobile ? 'small' : 'small'}
                            color="primary"
                            sx={{
                              padding: { xs: 0.5, sm: 1 },
                              '& .MuiSvgIcon-root': {
                                fontSize: { xs: '1rem', sm: '1.2rem' },
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="ユーザー詳細を表示">
                        <IconButton
                          onClick={() => handleViewProfile(user)}
                          size={isMobile ? 'small' : 'small'}
                          color="primary"
                          sx={{
                            padding: { xs: 0.5, sm: 1 },
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '1rem', sm: '1.2rem' },
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {error
                      ? 'データの読み込みに失敗しました'
                      : 'ユーザーが見つかりません'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount || 0}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} / ${count !== -1 ? count : 0}件`
          }
          labelRowsPerPage="表示件数:"
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
              minHeight: { xs: 80, sm: 52 },
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'flex' },
            },
            '& .MuiTablePagination-selectLabel': {
              margin: { xs: 0, sm: '0' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            },
            '& .MuiTablePagination-displayedRows': {
              margin: { xs: 0, sm: '0' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            },
          }}
        />
      </TableContainer>

      {/* ユーザー編集ダイアログ */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmall}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          ユーザー情報編集
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                <strong>氏名:</strong> {selectedUser.fullName}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                <strong>メールアドレス:</strong> {selectedUser.email}
              </Typography>

              <FormControl
                fullWidth
                sx={{ mb: 3 }}
                size={isMobile ? 'small' : 'medium'}
              >
                <InputLabel>権限</InputLabel>
                <Select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as UserRole)}
                  label="権限"
                >
                  <MenuItem value={UserRole.STUDENT}>一般ユーザー</MenuItem>
                  <MenuItem value={UserRole.COMPANY_ADMIN}>企業管理者</MenuItem>
                  <MenuItem value={UserRole.HR}>HR担当者</MenuItem>
                  <MenuItem value={UserRole.EMPLOYEE}>従業員</MenuItem>
                  <MenuItem value={UserRole.ADMIN}>管理者</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={editActive}
                    onChange={(e) => setEditActive(e.target.checked)}
                    size={isMobile ? 'medium' : 'medium'}
                  />
                }
                label="アカウントを有効にする"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* チャット履歴ダイアログ */}
      <Dialog
        open={chatHistoryOpen}
        onClose={handleCloseChatHistory}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: { xs: '100vh', md: '90vh' },
            maxHeight: { xs: '100vh', md: '90vh' },
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              チャット履歴
            </Typography>
            <IconButton
              onClick={handleCloseChatHistory}
              size={isMobile ? 'small' : 'medium'}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
          {chatHistoryUser && (
            <UserChatHistory
              userId={chatHistoryUser.id}
              userName={chatHistoryUser.fullName}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ユーザー詳細ダイアログ */}
      {profileDetailUserId && (
        <UserProfileDetailModal
          open={profileDetailOpen}
          onClose={handleCloseProfile}
          userId={profileDetailUserId}
        />
      )}
    </Box>
  );
};
